import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const LEVELS = ['Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond'];
const THRESHOLDS = { Bronze: 0, Silver: 100, Gold: 400, Platinum: 700, Diamond: 1000 };
const LEVEL_COLORS = {
  Bronze: '#cd7f32', Silver: '#94a3b8', Gold: '#f59e0b',
  Platinum: '#7c3aed', Diamond: '#06b6d4',
};

function getMembershipLevel(points) {
  if (points >= 1000) return 'Diamond';
  if (points >= 700) return 'Platinum';
  if (points >= 400) return 'Gold';
  if (points >= 100) return 'Silver';
  return 'Bronze';
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const { isAuthenticated, userInfo, fetchUserInfo, getAuthHeaders } = useAuth();

  const [formData, setFormData] = useState({ name: '', email: '', address: '', phone_number: '', picture: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    if (userInfo) {
      setFormData({ name: userInfo.name || '', email: userInfo.email || '', address: userInfo.address || '', phone_number: userInfo.phone_number || '', picture: userInfo.picture || '', password: '' });
    } else { fetchUserInfo?.(); }
  }, [isAuthenticated, userInfo, navigate, fetchUserInfo]);

  const points = userInfo?.points || 0;
  const level = getMembershipLevel(points);
  const levelColor = LEVEL_COLORS[level];
  const completion = userInfo?.profile_completion || 0;
  const R = 42;
  const circ = 2 * Math.PI * R;
  const dash = circ - (completion / 100) * circ;

  const nextLevel = LEVELS[LEVELS.indexOf(level) + 1];
  const nextThreshold = nextLevel ? THRESHOLDS[nextLevel] : 1000;
  const prevThreshold = THRESHOLDS[level];
  const segProgress = nextLevel ? Math.min(100, ((points - prevThreshold) / (nextThreshold - prevThreshold)) * 100) : 100;

  const handleChange = (e) => {
    setFormData(p => ({ ...p, [e.target.name]: e.target.value }));
    setError(''); setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError(''); setSuccess('');
    try {
      const body = { name: formData.name, email: formData.email, address: formData.address };
      if (formData.password.trim()) body.password = formData.password;
      const res = await fetch('https://aman-life-style-bd-main-server-974485829900.asia-south1.run.app/update_user_profile', {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok) { setSuccess('Profile updated successfully!'); await fetchUserInfo?.(); setFormData(p => ({ ...p, password: '' })); }
      else { setError(data.error || 'Update failed.'); }
    } catch { setError('Network error. Please try again.'); }
    finally { setLoading(false); }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Hind+Siliguri:wght@400;500;600;700&display=swap');
        .profile-page { background: #f5f5f5; min-height: 100vh; padding: 80px 0 40px; font-family: 'Hind Siliguri', sans-serif; color: #282c3f; }
        .profile-wrap { max-width: 960px; margin: 0 auto; padding: 0 16px; }
        .profile-page-title { font-size: 22px; font-weight: 700; color: #282c3f; margin-bottom: 20px; padding-bottom: 14px; border-bottom: 1px solid #e8e8e8; }
        .profile-grid { display: grid; grid-template-columns: 280px 1fr; gap: 16px; }
        @media (max-width: 720px) { .profile-grid { grid-template-columns: 1fr; } }

        /* card */
        .pcard { background: #fff; border: 1px solid #e8e8e8; border-radius: 4px; overflow: hidden; margin-bottom: 16px; }
        .pcard-head { padding: 14px 18px; border-bottom: 1px solid #f0f0f0; font-size: 13px; font-weight: 700; color: #282c3f; text-transform: uppercase; letter-spacing: 0.04em; }
        .pcard-body { padding: 18px; }

        /* avatar section */
        .avatar-section { display: flex; flex-direction: column; align-items: center; padding: 24px 18px 18px; }
        .avatar-ring { position: relative; width: 88px; height: 88px; margin-bottom: 12px; }
        .avatar-img { width: 72px; height: 72px; border-radius: 50%; object-fit: cover; position: absolute; top: 8px; left: 8px; }
        .avatar-placeholder { width: 72px; height: 72px; border-radius: 50%; background: #f0f0f0; display: flex; align-items: center; justify-content: center; position: absolute; top: 8px; left: 8px; }
        .avatar-name { font-size: 15px; font-weight: 700; color: #282c3f; margin-bottom: 2px; text-align: center; }
        .avatar-phone { font-size: 12px; color: #94a3b8; margin-bottom: 12px; }
        .completion-label { font-size: 11px; color: #94a3b8; margin-top: 4px; }

        /* level badge */
        .level-badge { display: inline-flex; align-items: center; gap: 6px; padding: 4px 14px; border-radius: 20px; font-size: 12px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; border: 1.5px solid; margin: 8px auto; }

        /* progress bar */
        .bar-track { height: 6px; background: #f0f0f0; border-radius: 3px; overflow: hidden; margin: 10px 0 6px; }
        .bar-fill { height: 100%; border-radius: 3px; transition: width 0.5s ease; }
        .bar-labels { display: flex; justify-content: space-between; font-size: 10px; color: #94a3b8; }

        /* points display */
        .points-big { font-size: 36px; font-weight: 700; color: #282c3f; line-height: 1; }
        .points-sub { font-size: 12px; color: #94a3b8; margin-top: 2px; }

        /* tabs */
        .tabs { display: flex; border-bottom: 2px solid #f0f0f0; margin-bottom: 0; }
        .tab-btn { padding: 12px 20px; font-size: 13px; font-weight: 600; color: #94a3b8; background: none; border: none; cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -2px; transition: all 0.15s; font-family: inherit; }
        .tab-btn.active { color: #ff3f6c; border-bottom-color: #ff3f6c; }
        .tab-btn:hover:not(.active) { color: #282c3f; }

        /* form */
        .form-group { margin-bottom: 16px; }
        .form-label { display: block; font-size: 12px; font-weight: 600; color: #282c3f; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.04em; }
        .form-input { width: 100%; padding: 10px 14px; border: 1.5px solid #e8e8e8; border-radius: 4px; font-size: 14px; font-family: inherit; color: #282c3f; outline: none; transition: border-color 0.2s; box-sizing: border-box; }
        .form-input:focus { border-color: #ff3f6c; }
        .form-input:disabled { background: #fafafa; color: #94a3b8; cursor: not-allowed; }
        .form-hint { font-size: 11px; color: #94a3b8; margin-top: 4px; }
        .submit-btn { width: 100%; padding: 12px; background: #ff3f6c; color: #fff; border: none; border-radius: 4px; font-size: 14px; font-weight: 700; cursor: pointer; font-family: inherit; letter-spacing: 0.02em; transition: background 0.2s; margin-top: 4px; }
        .submit-btn:hover:not(:disabled) { background: #e0355e; }
        .submit-btn:disabled { background: #e8e8e8; color: #94a3b8; cursor: not-allowed; }

        /* alerts */
        .alert { padding: 10px 14px; border-radius: 4px; font-size: 13px; font-weight: 500; margin-bottom: 14px; }
        .alert-error { background: #fff0f3; border: 1px solid #ffd0d8; color: #e53935; }
        .alert-success { background: #f0fdf4; border: 1px solid #bbf7d0; color: #15803d; }

        /* info grid */
        .info-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 0; border-bottom: 1px solid #f5f5f5; font-size: 13px; }
        .info-row:last-child { border-bottom: none; }
        .info-key { color: #94a3b8; font-weight: 500; }
        .info-val { color: #282c3f; font-weight: 600; text-align: right; }
      `}</style>

      <div className="profile-page">
        <div className="profile-wrap">
          <h1 className="profile-page-title">My Account</h1>
          <div className="profile-grid">

            {/* ── Left ── */}
            <div>
              {/* Avatar card */}
              <div className="pcard">
                <div className="avatar-section">
                  <div className="avatar-ring">
                    <svg width="88" height="88" viewBox="0 0 88 88" style={{ position: 'absolute', top: 0, left: 0 }}>
                      <circle cx="44" cy="44" r={R} fill="none" stroke="#f0f0f0" strokeWidth="4" />
                      <circle cx="44" cy="44" r={R} fill="none" stroke="#10b981" strokeWidth="4"
                        strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={dash}
                        transform="rotate(-90 44 44)" style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
                    </svg>
                    {formData.picture
                      ? <img src={formData.picture} alt="Avatar" className="avatar-img" />
                      : <div className="avatar-placeholder">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="#ccc">
                            <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
                          </svg>
                        </div>
                    }
                  </div>
                  <div className="avatar-name">{formData.name || 'Guest User'}</div>
                  <div className="avatar-phone">{formData.phone_number || '—'}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#10b981' }}>{completion}%</div>
                  <div className="completion-label">Profile Complete</div>
                </div>
              </div>

              {/* Points & Level */}
              <div className="pcard">
                <div className="pcard-head">Points & Membership</div>
                <div className="pcard-body">
                  <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    <div className="points-big">{points.toLocaleString()}</div>
                    <div className="points-sub">Total Points</div>
                    <div className="level-badge" style={{ color: levelColor, borderColor: levelColor, background: levelColor + '15' }}>
                      ★ {level}
                    </div>
                  </div>

                  {nextLevel && (
                    <>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
                        {nextThreshold - points} pts to {nextLevel}
                      </div>
                      <div className="bar-track">
                        <div className="bar-fill" style={{ width: `${segProgress}%`, background: levelColor }} />
                      </div>
                      <div className="bar-labels">
                        <span>{prevThreshold}</span>
                        <span>{nextThreshold}</span>
                      </div>
                    </>
                  )}

                  <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between' }}>
                    {LEVELS.map(l => (
                      <div key={l} style={{ textAlign: 'center', flex: 1 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: points >= THRESHOLDS[l] ? LEVEL_COLORS[l] : '#e8e8e8', margin: '0 auto 3px' }} />
                        <div style={{ fontSize: 9, color: points >= THRESHOLDS[l] ? LEVEL_COLORS[l] : '#ccc', fontWeight: 600 }}>{l.slice(0, 2)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Right ── */}
            <div>
              <div className="pcard">
                <div className="tabs">
                  {[['profile', 'Edit Profile'], ['info', 'Account Info']].map(([t, label]) => (
                    <button key={t} className={`tab-btn ${activeTab === t ? 'active' : ''}`} onClick={() => setActiveTab(t)}>{label}</button>
                  ))}
                </div>

                <div style={{ padding: '20px 18px' }}>
                  {activeTab === 'profile' && (
                    <form onSubmit={handleSubmit}>
                      {error && <div className="alert alert-error">{error}</div>}
                      {success && <div className="alert alert-success">{success}</div>}

                      <div className="form-group">
                        <label className="form-label">Full Name</label>
                        <input className="form-input" type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Enter your full name" />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Email Address</label>
                        <input className="form-input" type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Enter your email" />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Delivery Address</label>
                        <input className="form-input" type="text" name="address" value={formData.address} onChange={handleChange} placeholder="Enter your address" />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Mobile Number</label>
                        <input className="form-input" type="text" name="phone_number" value={formData.phone_number} disabled />
                        <div className="form-hint">Phone number cannot be changed</div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">New Password</label>
                        <input className="form-input" type="password" name="password" value={formData.password} onChange={handleChange} placeholder="Leave blank to keep current password" />
                        <div className="form-hint">Only fill if you want to change your password</div>
                      </div>

                      <button className="submit-btn" type="submit" disabled={loading}>
                        {loading
                          ? <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                              <svg style={{ animation: 'spin 0.8s linear infinite' }} width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25"/>
                                <path fill="currentColor" opacity="0.75" d="M4 12a8 8 0 018-8V0C5.37 0 0 5.37 0 12h4z"/>
                              </svg>
                              Saving...
                            </span>
                          : 'Save Changes'
                        }
                      </button>
                      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                    </form>
                  )}

                  {activeTab === 'info' && (
                    <div>
                      {[
                        ['Name', formData.name || '—'],
                        ['Email', formData.email || '—'],
                        ['Phone', formData.phone_number || '—'],
                        ['Address', formData.address || '—'],
                        ['Membership Level', level],
                        ['Points', points.toLocaleString()],
                        ['Profile Completion', `${completion}%`],
                      ].map(([k, v]) => (
                        <div className="info-row" key={k}>
                          <span className="info-key">{k}</span>
                          <span className="info-val">{v}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}